import { ChartColumn, Sparkles } from 'lucide-react';

import { DashboardPage } from '@/components/app/dashboard-page';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useActiveOrganization } from '@/features/organization';
import { useActiveLocation } from '@/features/organization-locations/use-active-location';

import {
  type ReportPreset,
  daysInWindow,
  formatWindow,
  presetWindow,
  reportPresets,
} from '../lib/report-window';
import { BusinessReportView } from './business-report';
import { ClaireReportView } from './claire-report';

export type ReportTab = 'business' | 'claire';

const ALL_LOCATIONS = 'all';

export function ReportsPage({
  tab,
  preset,
  locationId,
  onChange,
}: {
  tab: ReportTab;
  preset: ReportPreset;
  locationId?: string;
  onChange: (next: {
    tab?: ReportTab;
    range?: ReportPreset;
    location?: string;
  }) => void;
}) {
  const { data: organization } = useActiveOrganization();
  const { locations, isMultiLocation } = useActiveLocation();
  const timeZone = organization?.timezone || 'UTC';
  const reportWindow = { ...presetWindow(preset, timeZone), locationId };
  const days = daysInWindow(reportWindow.from, reportWindow.to);
  const locationName = locationId
    ? locations.find((l) => l.id === locationId)?.name
    : null;

  return (
    // One Tabs root around the whole page, so each trigger in the toolbar
    // controls a real panel below it (aria-controls must point somewhere).
    // `contents` keeps the root out of the layout.
    <Tabs
      value={tab}
      onValueChange={(value) => onChange({ tab: value as ReportTab })}
      className="contents"
    >
      <DashboardPage
        title="Reports"
        width="wide"
        contentClassName="max-md:px-4 max-md:pb-8"
        description={`${locationName ?? (isMultiLocation ? 'All locations' : (organization?.name ?? ''))} · ${formatWindow(reportWindow.from, reportWindow.to)}`}
        toolbar={
          <div className="flex flex-col gap-4">
            <TabsList>
              <TabsTrigger value="business">
                <ChartColumn aria-hidden />
                Business
              </TabsTrigger>
              <TabsTrigger value="claire">
                <Sparkles aria-hidden />
                Claire Dashboard
              </TabsTrigger>
            </TabsList>
            <div className="flex flex-wrap gap-2">
              <Select
                value={preset}
                onValueChange={(value) =>
                  onChange({ range: value as ReportPreset })
                }
              >
                <SelectTrigger
                  size="sm"
                  className="w-[160px]"
                  aria-label="Period"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {reportPresets.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isMultiLocation ? (
                <Select
                  value={locationId ?? ALL_LOCATIONS}
                  onValueChange={(value) =>
                    onChange({
                      location: value === ALL_LOCATIONS ? undefined : value,
                    })
                  }
                >
                  <SelectTrigger
                    size="sm"
                    className="w-[200px]"
                    aria-label="Location"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_LOCATIONS}>All locations</SelectItem>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name ?? 'Unnamed location'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              <span className="self-center text-muted-foreground text-xs">
                Changes compare with the {days} days before
              </span>
            </div>
          </div>
        }
      >
        <TabsContent value="business">
          <BusinessReportView range={reportWindow} />
        </TabsContent>
        <TabsContent value="claire">
          <ClaireReportView range={reportWindow} />
        </TabsContent>
      </DashboardPage>
    </Tabs>
  );
}
